import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Clock, CalendarDays, UserRound, GraduationCap, Target } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://wingacademybrasil.com.br';

const mdComponents = {
  h2: (props) => <h2 className="text-2xl font-black text-white mt-10 mb-4" {...props} />,
  h3: (props) => <h3 className="text-xl font-bold text-white mt-8 mb-3" {...props} />,
  p: (props) => <p className="text-gray-300 leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 space-y-2 text-gray-300 mb-4" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 space-y-2 text-gray-300 mb-4" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="text-white font-bold" {...props} />,
  a: (props) => <a className="text-yellow-400 hover:text-yellow-300 underline underline-offset-2" {...props} />,
  blockquote: (props) => (
    <blockquote className="border-l-4 border-yellow-500 bg-yellow-500/5 px-5 py-3 rounded-r-lg my-6 text-gray-300 [&>p]:mb-0" {...props} />
  ),
  code: (props) => <code className="bg-gray-800 text-yellow-300 px-1.5 py-0.5 rounded text-sm" {...props} />,
  img: (props) => <img className="w-full rounded-xl border border-gray-800 my-6" loading="lazy" alt="" {...props} />,
  table: (props) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border border-gray-800 rounded-lg" {...props} /></div>,
  thead: (props) => <thead className="bg-gray-900 text-yellow-400" {...props} />,
  th: (props) => <th className="text-left px-4 py-2 border-b border-gray-800 font-bold" {...props} />,
  td: (props) => <td className="px-4 py-2 border-b border-gray-800/60 text-gray-300" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${BACKEND_URL}/api/blog/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4">
        <SEO title="Artigo não encontrado" path={`/blog/${slug}`} noindex />
        <h1 className="text-3xl font-black text-white mb-4">Artigo não encontrado</h1>
        <Link to="/blog" className="text-yellow-400 hover:text-yellow-300 font-medium">← Voltar para o Blog</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.titulo,
    description: post.resumo,
    url: `${SITE_URL}/blog/${post.slug}`,
    datePublished: post.data_criacao,
    dateModified: post.data_atualizacao || post.data_criacao,
    inLanguage: 'pt-BR',
    author: { '@type': 'Person', name: post.autor, jobTitle: 'Técnico de Áudio e Endorsee Behringer Brasil' },
    publisher: { '@type': 'Organization', name: 'WING Academy Brasil', url: SITE_URL },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  const jsonLd = [articleJsonLd];
  if (post.faq && post.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: post.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <div className="min-h-screen bg-black">
      <SEO
        title={post.titulo}
        description={post.resumo}
        path={`/blog/${post.slug}`}
        breadcrumbs={[
          { name: 'Blog', path: '/blog' },
          { name: post.titulo, path: `/blog/${post.slug}` },
        ]}
        jsonLd={jsonLd}
      />

      {/* Header simples */}
      <div className="border-b border-gray-800 bg-gray-950">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/blog" data-testid="post-back-blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" /> Todos os artigos
          </Link>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 py-12" data-testid="blog-post-article">
        <span className="bg-yellow-500/15 text-yellow-400 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
          {post.categoria}
        </span>
        <h1 className="text-3xl sm:text-4xl font-black text-white mt-4 mb-4 leading-tight">{post.titulo}</h1>
        <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm mb-10 pb-8 border-b border-gray-800">
          <span className="inline-flex items-center gap-1.5"><UserRound className="w-4 h-4" /> {post.autor}</span>
          <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {new Date(post.data_criacao).toLocaleDateString('pt-BR')}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.tempo_leitura} min de leitura</span>
        </div>

        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{post.conteudo}</ReactMarkdown>

        {/* CTA final */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/#inscricao"
            data-testid="post-cta-mentoria"
            className="flex items-center gap-4 bg-yellow-500 hover:bg-yellow-400 text-black rounded-xl p-5 transition-colors"
          >
            <GraduationCap className="w-8 h-8 flex-shrink-0" />
            <div>
              <div className="font-black text-sm uppercase">Mentoria WING</div>
              <div className="text-xs opacity-80">Aprenda com Adeilson Chagas</div>
            </div>
          </Link>
          <Link
            to="/quiz"
            data-testid="post-cta-quiz"
            className="flex items-center gap-4 bg-gray-900 hover:bg-gray-800 border border-yellow-500/40 text-white rounded-xl p-5 transition-colors"
          >
            <Target className="w-8 h-8 text-yellow-400 flex-shrink-0" />
            <div>
              <div className="font-black text-sm uppercase">Quiz Gratuito</div>
              <div className="text-xs text-gray-400">Teste seu nível na WING</div>
            </div>
          </Link>
        </div>
      </article>
    </div>
  );
}
